/**
 * `<PopMenu>` — a trigger button plus a page-drawn popup panel, closed by an
 * outside click or Escape.
 *
 * Why not a native `<select>` / `<details>`: a `<select>`'s POPUP is drawn by
 * the OS, not the page. On a Mac in dark mode it opens as a dark grey panel
 * with oversized system type over a light dashboard; `color-scheme: light`
 * fixes that in Chrome but NOT in Safari, where the popup is a real NSMenu and
 * ignores CSS entirely. Drawing the panel ourselves is the only way the
 * studio's styling always wins.
 *
 * `children` is a render prop taking `close` so a menu row can dismiss the
 * panel as it acts.
 *
 * CONSTRAINT — a row may not own state that must OUTLIVE the click. The panel
 * is `{open ? children(close) : null}`, so `close()` unmounts every child; a row
 * that opened its own modal takes that modal down with it in the same React
 * batch, and nothing paints (a portal does not save it — the component itself
 * unmounts). This is real: it is how `Create coupon` and `Export data` silently
 * died in 4a4343f9 when they moved into this menu as `<ActionModalButton>`.
 * Such a row must be a plain `<button>` that calls `close()` and flips state on
 * the HOST, which then renders the modal outside this subtree — see
 * `MemberDetail`'s `<ActionModal>` pair.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";

export interface PopMenuProps {
  /** Accessible name for the trigger (also its `title`). */
  readonly label: string;
  /** Trigger face — a glyph, a caret, the current filter value. */
  readonly trigger: ReactNode;
  /** Extra classes on the trigger button (its size/skin comes from the host). */
  readonly triggerClassName?: string;
  /**
   * Which edge the panel hangs from. `right` (default) suits a trigger at the
   * right of its row; `left` avoids a panel hanging off the card when the
   * trigger sits at the left.
   */
  readonly align?: "left" | "right";
  readonly disabled?: boolean;
  readonly children: (close: () => void) => ReactNode;
}

export function PopMenu({
  label,
  trigger,
  triggerClassName,
  align = "right",
  disabled,
  children,
}: PopMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      if (ref.current !== null && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="pop-menu" ref={ref}>
      <button
        type="button"
        className={`${triggerClassName ?? ""}${open ? " open" : ""}`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={label}
        title={label}
        disabled={disabled === true}
        onClick={() => setOpen((v) => !v)}
      >
        {trigger}
      </button>
      {open && disabled !== true ? (
        <div className={`pop-menu-list pop-menu-list--${align}`} role="menu">
          {children(() => setOpen(false))}
        </div>
      ) : null}
    </div>
  );
}
